import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalizedPath } from "@/i18n/useLocalizedPath";
import { GradientOrbs, DotGrid } from "./BackgroundFX";
import { DispatchMapPanel } from "./DispatchMapPanel";
import { fadeUp, stagger, viewportOnce } from "./motion-presets";

const TRADE_KEYS = ["hvac", "plumbing", "electrical", "garageDoors", "locksmiths"] as const;

export const HeroSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("landing");
  const lp = useLocalizedPath();

  return (
    <section className="relative overflow-hidden pt-20 pb-24 md:pt-28 md:pb-32">
      <GradientOrbs />
      <DotGrid />

      <div className="relative container mx-auto px-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="mx-auto max-w-3xl text-center"
        >
          <motion.div variants={fadeUp} className="flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="relative inline-flex">
                <span className="absolute inset-0 rounded-full bg-success animate-ping opacity-60" />
                <span className="relative h-1.5 w-1.5 rounded-full bg-success" />
              </span>
              <span className="text-foreground/85">{t("hero.badge")}</span>
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-foreground leading-[1.05]"
          >
            {t("hero.titleLine1")}
            <br />
            <span className="text-muted-foreground">{t("hero.titleLine2")}</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center"
          >
            <Button
              size="lg"
              onClick={() => navigate(lp("/auth") + "?mode=signup")}
              className="btn-gradient text-white border-0 h-11 px-6 gap-2"
            >
              {t("hero.ctaPrimary")} <ArrowRight className="h-4 w-4" />
            </Button>
            <Button asChild size="lg" variant="outline" className="h-11 px-5">
              <a href="#how-it-works">{t("hero.ctaSecondary")}</a>
            </Button>
          </motion.div>

          <motion.p variants={fadeUp} className="mt-4 text-xs text-muted-foreground">
            {t("hero.trialNote")}
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-10 flex items-center justify-center gap-x-5 gap-y-2 flex-wrap text-xs text-muted-foreground"
          >
            <span className="uppercase tracking-wider text-muted-foreground/60">{t("hero.builtFor")}</span>
            {TRADE_KEYS.map((key, i) => (
              <span key={key} className="flex items-center gap-x-5">
                <span className="text-foreground/70">{t(`hero.trades.${key}`)}</span>
                {i < TRADE_KEYS.length - 1 && <span className="text-border">·</span>}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <HeroVisual />
        <TrustBar />
      </div>
    </section>
  );
};

const STORE_LINKS = [
  { key: "appStore", href: "https://apps.apple.com/us/app/workloop-services/id6761512706" },
  { key: "googlePlay", href: "https://play.google.com/store/apps/details?id=com.cmacha2.workloopclient" },
] as const;

const TrustBar = () => {
  const { t } = useTranslation("landing");
  const chip =
    "inline-flex items-center gap-1.5 rounded-full border border-border bg-card/40 px-3 py-1.5 text-xs text-muted-foreground backdrop-blur";
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={fadeUp}
      className="mt-10 flex flex-wrap items-center justify-center gap-2.5"
    >
      {STORE_LINKS.map(({ key, href }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`${chip} hover:text-foreground hover:border-border/80 transition-colors`}
        >
          {t(`hero.trust.${key}`)} <ArrowUpRight className="h-3 w-3" />
        </a>
      ))}
      <span className={chip}>{t("hero.trust.quickbooks")}</span>
      <span className={chip}>{t("hero.trust.stripe")}</span>
    </motion.div>
  );
};

const HeroVisual = () => {
  const { t } = useTranslation("landing");
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      viewport={viewportOnce}
      className="relative mt-16 md:mt-20 mx-auto max-w-4xl"
    >
      <div className="relative rounded-2xl border border-border bg-card/50 p-1.5 backdrop-blur shadow-2xl shadow-black/30">
        <div className="rounded-xl border border-border bg-background overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-card/30">
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-muted" />
              <span className="h-2.5 w-2.5 rounded-full bg-muted" />
              <span className="h-2.5 w-2.5 rounded-full bg-muted" />
            </div>
            <div className="hidden sm:block text-xs text-muted-foreground">app.workloop.us</div>
            <div className="w-12" />
          </div>
          <DispatchMapPanel />
        </div>
      </div>

      {/* Real customer-app screenshot — the one thing competitors can't show. */}
      <div className="absolute -bottom-8 right-3 sm:-right-6 w-32 sm:w-40 md:w-48 rotate-2">
        <div className="rounded-[1.75rem] border-[5px] border-zinc-900 bg-zinc-900 overflow-hidden shadow-2xl shadow-black/50">
          <img
            src="/screenshots/customer-app-tracking.jpg"
            alt={t("hero.mockup.phoneAlt")}
            width="560"
            height="1241"
            decoding="async"
            className="block w-full h-auto"
          />
        </div>
      </div>
    </motion.div>
  );
};

